import { flow, O, pipe, RA, RR, S } from '@packages/prelude'
import { Lens } from 'monocle-ts'
import * as domain from '@packages/domain'
import * as GreetingField from '~/components/ProductPreview/state/GreetingField'
import * as UserImageField from '~/components/ProductPreview/state/UserImageField'

export interface BuildProductConfigArgs {
  productId: number
  quantity: number
  previewId: O.Option<string>
  formatEntry: O.Option<readonly [string, string]>
  segments: ReadonlyArray<domain.AudioSegment.Type>
  starGreeting: RR.ReadonlyRecord<string, GreetingField.Type>
  userImage: RR.ReadonlyRecord<string, UserImageField.UserImageData>
}

// Record<id, V> → [fieldId, value] for every entry whose value (focused by the Lens) is Some.
const entriesBy = <V extends { fieldId: string }, A extends string>(value: Lens<V, O.Option<A>>) =>
  flow(
    RR.values<V>,
    RA.filterMap((v: V) =>
      pipe(
        value.get(v),
        O.map(a => [v.fieldId, a] as const)
      )
    )
  )

export const selectionOf = (args: BuildProductConfigArgs): RR.ReadonlyRecord<string, string> => {
  const segmentEntries: ReadonlyArray<readonly [string, string]> = pipe(
    args.segments,
    RA.filterMap(
      domain.AudioSegment.ADT.matchStrict({
        preRecorded: s =>
          pipe(
            s.selectedName,
            O.map(n => [s.fieldId, n] as const)
          ),
        templateName: s =>
          pipe(
            s.name,
            O.map(n => [s.fieldId, n] as const)
          ),
        userUpload: s =>
          pipe(
            s.remoteUrl,
            O.map(u => [s.fieldId, u] as const)
          ),
        aiGreeting: s =>
          pipe(
            s.remoteUrl,
            O.map(u => [s.fieldId, u] as const)
          )
      })
    )
  )

  const entries: ReadonlyArray<readonly [string, string]> = [
    ...RA.fromOption(args.formatEntry),
    ...segmentEntries,
    ...entriesBy(GreetingField.Lens.greeting)(args.starGreeting),
    ...entriesBy(UserImageField.remoteUrlLens)(args.userImage)
  ]

  return RR.fromFoldableMap(S.last<string>(), RA.Foldable)(entries, ([k, v]) => [k, v])
}

export const buildProductConfig = (args: BuildProductConfigArgs): domain.Api.ProductConfig.Type => {
  const fields = selectionOf(args)

  return { productId: args.productId, quantity: args.quantity, previewId: args.previewId, fields }
}

export const toRequestBody = (config: domain.Api.ProductConfig.Type): BodyInit => {
  const body = new URLSearchParams()
  body.set('add-to-cart', String(config.productId))
  body.set('quantity', String(config.quantity))
  body.set('product_config', JSON.stringify(domain.Api.ProductConfig.Type.encode(config)))

  return body
}
